import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withStyles, Dialog } from "@material-ui/core";
import { ExpandMore, ExpandLess, SentimentSatisfied } from "@material-ui/icons";
import { withTranslation } from "react-i18next";

// Components
import StepTracker from "../components/UpgradePage/StepTracker";
import PhoneVerification from "../components/UpgradePage/PhoneVerification";
import IdentityVerification from "../components/UpgradePage/IdentityVerification";
import Profile from "../components/UpgradePage/Profile";
import IdUpload from "../components/UpgradePage/IdUpload";
import AddressVerification from "../components/UpgradePage/AddressVerification";
import Loader from "../components/Loader";
import Experience from "../components/UpgradePage/Experience";
import Level3 from "../components/UpgradePage/Level3";
import DocumentReview from "../components/UpgradePage/DocumentReview";
import { images } from "../utils/images";

// Actions
import {
  setNewLang,
  showVerificationScreen,
  getAcceptedIDtypes,
  uploadToJumio,
  updateProfile,
  continueVerification,
  similarProfileToggle,
  pushNotification,
} from "../actions/index";

const jumioCallbackUrl = (
  process.env.NODE_ENV === "production" ? window.SOLO.env : process.env
).REACT_APP_JUMIO_CALLBACK_URL;

const termsUrl = (
  process.env.NODE_ENV === "production" ? window.SOLO.env : process.env
).REACT_APP_TERMS_URL;
const privacyUrl = (
  process.env.NODE_ENV === "production" ? window.SOLO.env : process.env
).REACT_APP_PRIVACY_URL;

const urlSolo = (
  process.env.NODE_ENV === "production" ? window.SOLO.env : process.env
).REACT_APP_DASHBOARD;

const langs = [
  { name: "English", val: "en" },
  { name: "Spanish", val: "es" },
];

function UpgradeContainer(props) {
  const {
    t,
    classes,
    i18n,
    lang,
    account,
    onDialog,
    closeDialog,
    similarProfile,
    jumioUploadFinished,
    level6UploadFinished,
  } = props;
  const [step, setStep] = useState(1);
  const [langSelectorOpen, toggleLangSelector] = useState(false);
  const [nationality, setNationality] = useState("");
  const [selfie, addSelfie] = useState("");
  const [idPhotos, addIdPhotos] = useState(null);
  const [profile, addProfile] = useState(null);
  const [idType, addIdType] = useState(null);
  const [issuingCountry, addIssuingCountry] = useState(null);
  const [jumioRequest, triggerJumioRequest] = useState(false);
  const [submitLevel6App, triggerLevel6] = useState(false);
  const [proofOfAddress, addProofOfAddress] = useState(null);
  const [experience, addExperience] = useState(null);
  const [openCancelConfirmation, setCancelConfirmation] = useState(false);
  const [isSimilarProfile, setSimilarProfile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Open Confirmation modal
  const exit = () => {
    setCancelConfirmation(true);
  };

  // Cancel Verification
  const cancelVerification = () => {
    // props.showVerificationScreen(false);

    props.continueVerification(false);
    props.history.push("/id/settings");
  };

  // Move forward in the flow
  const moveForward = (e) => {
    if (e) {
      if (step === 3.5 || step === 4.5) {
        step = Number(String(step).split(".")[0]) + 1;

        return setStep(step);
      }
      setStep(step + 1);
    }
  };

  // Change language
  const changeLanguage = (newLang) => {
    toggleLangSelector(false);
    props.setNewLang(newLang);
  };

  // ComponentDidMount
  useEffect(() => {
    document.body.style.background = "#262626";
    props.history.push("/id/settings");

    return () => {
      document.body.style.background = "#262626";
      setStep(1);
    };
  }, []);

  // Account Change
  useEffect(
    function () {
      if (account.account !== null) {
        let status = account.account.profile
          ? account.account.profile.status
          : account.account.level;

        switch (status) {
          case 1:
            setStep(1);
            break;
          case 2:
            setStep(2);
            break;
          case "level_2":
            setStep(3);
            break;
          case "level_2_auto_rejected":
            if (account.account.documents.length === 0) {
              setSimilarProfile(true);
              setStep(3.5);
            } else {
              if (account.account.profile.level_6_application_submitted_at) {
                props.history.push("/id/settings");
              } else {
                setStep(5);
              }
            }
            break;
          case "level_2_new_document":
            if (account.account.profile.proof_of_address_document) {
              setStep(7);
            } else {
              if (account.account.profile.level_6_application_submitted_at) {
                props.history.push("/id/settings");
              } else {
                setStep(5);
              }
            }
            break;
          case "level_3":
            setStep(3.5);
            break;
          case "level_4":
            setStep(5);
            break;
          case "level_4_pending":
            setStep(5);
            break;
          case "level_5":
            setStep(5);
            break;
          case "level_6_pending":
            setStep(7);
            break;
          case "level_6":
            props.history.push("/id/settings");
            break;
          default:
            props.history.push("/id/settings");
        }

        setLoading(false);
      }
    },
    [account]
  );

  // Change Language
  useEffect(
    function () {
      i18n.changeLanguage(lang);
    },
    [lang]
  );

  // Trigger Jumio Request
  useEffect(
    function () {
      if (jumioRequest) {
        let userProfile = profile === null ? account.account.profile : profile;

        let jumioObj = {
          merchantIdScanReference: `${userProfile.first_name}_${userProfile.first_last_name}_${idType.idType}`,
          faceImage: selfie.replace(/^data:image\/(png|jpg|jpeg);base64,/, ""),
          idType: idType.idType,
          frontsideImage: idPhotos.idFront.replace(
            /^data:image\/(png|jpg|jpeg);base64,/,
            ""
          ),
          customerId: account.account.uid,
          callbackUrl: jumioCallbackUrl,
          country: issuingCountry.countryCode,
        };

        if (idType.acquisitionConfig.backSide) {
          jumioObj.backsideImage = idPhotos.idBack.replace(
            /^data:image\/(png|jpg|jpeg);base64,/,
            ""
          );
        }

        // setStep(5);
        props.uploadToJumio(jumioObj);
      }
    },
    [jumioRequest]
  );

  // Move after Jumio finished
  useEffect(() => {
    if (jumioUploadFinished.sent) {
      if (jumioUploadFinished.finished) {
        setUploading(false);
        if (jumioUploadFinished.error) {
          triggerJumioRequest(false);
          props.pushNotification({
            message: "somethingWentWrong",
            type: "error",
          });
        } else {
          setStep(5);
        }
      } else {
        setUploading(true);
      }
    }
  }, [jumioUploadFinished]);

  // Trigger Request for level 6
  useEffect(() => {
    if (submitLevel6App) {
      let level6Application = {
        ...proofOfAddress,
        ...experience,
      };

      props.updateProfile(level6Application);
    }
  }, [submitLevel6App]);

  // Move after level 6 done
  useEffect(() => {
    if (level6UploadFinished.sent) {
      if (level6UploadFinished.finished) {
        setUploading(false);
        if (level6UploadFinished.error) {
          triggerLevel6(false);
          props.pushNotification({
            message: "somethingWentWrong",
            type: "error",
          });
        } else {
          setStep(7);
        }
      } else {
        setUploading(true);
      }
    }
  }, [level6UploadFinished]);

  // Similar Profile
  useEffect(() => {
    if (similarProfile) {
      setSimilarProfile(true);
    }
  }, [similarProfile]);

  const goToDocs = () => {
    setSimilarProfile(false);
    props.similarProfileToggle(false);
    props.continueVerification(false);
    props.history.push("/id/settings", { to_docs: true });
  };

  if (account.account === null || loading) {
    return <Loader />;
  }

  return (
    <div className={classes.upgradeContainer}>
      <div className={classes.soloLogo}>
        <img
          style={{ cursor: "pointer" }}
          src={images.logos.sologenicWhiteLogo}
          onClick={() => (window.location.href = urlSolo)}
        />
      </div>
      {step === 4.5 || step === 3.5 || step === 7 ? (
        ""
      ) : (
        <StepTracker step={step} />
      )}
      {step === 4.5 || step === 3.5 || step === 7 ? (
        ""
      ) : (
        <div className="exitBtn">
          <button
            onClick={() => {
              exit();
            }}
          >
            {t("exit")}
          </button>
        </div>
      )}

      <div className="stepContent">
        {step === 1 ? (
          <PhoneVerification
            setNationality={setNationality}
            moveForward={moveForward}
          />
        ) : step === 2 ? (
          <Profile
            nationality={nationality}
            moveForward={moveForward}
            addProfile={addProfile}
          />
        ) : step === 3 ? (
          <IdentityVerification
            moveForward={moveForward}
            addSelfie={addSelfie}
          />
        ) : step === 4 ? (
          <IdUpload
            addIdPhotos={addIdPhotos}
            addIssuingCountry={addIssuingCountry}
            addIdType={addIdType}
            moveForward={moveForward}
            triggerJumioRequest={triggerJumioRequest}
          />
        ) : step === 5 ? (
          <AddressVerification
            moveForward={moveForward}
            addProofOfAddress={addProofOfAddress}
          />
        ) : step === 6 ? (
          <Experience
            addExperience={addExperience}
            triggerLevel6={triggerLevel6}
          />
        ) : step === 3.5 ? (
          <Level3 router={props.history} isSimilar={isSimilarProfile} />
        ) : step === 4.5 || step === 7 ? (
          <DocumentReview />
        ) : (
          ""
        )}
      </div>
      <div className="links">
        {onDialog ? (
          ""
        ) : (
          <div className="selectLang">
            {/* <label>{t("selectLanguage")}:</label>
            <div className="languageSelector">
              <div
                className="selectorBtn"
                onClick={() => toggleLangSelector(!langSelectorOpen)}>
                <p>{lang.toUpperCase()}</p>
                {langSelectorOpen ? <ExpandMore /> : <ExpandLess />}
              </div>
              {langSelectorOpen ? (
                <div className="languagesContainer">
                  {langs.map((l, idx) => {
                    return (
                      <p
                        key={idx}
                        className={lang === l.val ? "activeLang" : ""}
                        onClick={() => changeLanguage(l.val)}>
                        {l.name}
                      </p>
                    );
                  })}
                </div>
              ) : (
                ""
              )}
            </div> */}
          </div>
        )}
        <div className="legalLinks">
          <a href={privacyUrl} target="_blank" rel="noopener noreferrer">
            {t("privacyPolicy")}
          </a>
          <a href={termsUrl} target="_blank" rel="noopener noreferrer">
            {t("termsAndConditions")}
          </a>
        </div>
      </div>
      <Dialog
        open={openCancelConfirmation}
        classes={{ paper: classes.cancelConfirmation }}
      >
        <div className="cancelHeader profileHeader">
          <p>{t("confirmation")}</p>
        </div>
        <div className="cancelBody">
          <p dangerouslySetInnerHTML={{ __html: t("sureToCancel") }} />
          <div className="btns">
            <button
              className="cancelBtn"
              onClick={() => setCancelConfirmation(false)}
            >
              {t("cancel")}
            </button>
            <button
              className="confirmBtn"
              onClick={() => {
                setCancelConfirmation(false);
                onDialog ? closeDialog() : cancelVerification();
              }}
            >
              {t("confirm")}
            </button>
          </div>
        </div>
      </Dialog>
      <Dialog
        open={similarProfile}
        classes={{ paper: classes.cancelConfirmation }}
      >
        <div className="cancelHeader">
          <p>{t("_similarProfileT")}</p>
        </div>
        <div className="cancelBody">
          <p>{t("_similarProfileP")}</p>
          <div className="btns profileBtns">
            <button className="confirmBtn" onClick={() => goToDocs()}>
              {t("goToSettingsDash")}
            </button>
          </div>
        </div>
      </Dialog>
      <Dialog open={uploading} classes={{ paper: classes.cancelConfirmationM }}>
        <div className="cancelHeader">
          <p>{t("_uploading")}</p>
        </div>
        <div className="cancelBody">
          <Loader />
        </div>
      </Dialog>
    </div>
  );
}

function mapStateToProps(state) {
  return {
    lang: state.lang,
    account: state.account,
    similarProfile: state.similarProfile,
    jumioUploadFinished: state.jumioUploadFinished,
    level6UploadFinished: state.level6UploadFinished,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      setNewLang,
      showVerificationScreen,
      getAcceptedIDtypes,
      uploadToJumio,
      updateProfile,
      continueVerification,
      similarProfileToggle,
      pushNotification,
    },
    dispatch
  );
}

const styles = (theme) => ({
  soloLogo: {
    width: "100%",
    height: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    position: "relative",
    "& img": {
      width: 180,
      height: "auto",
    },
  },
  upgradeContainer: {
    paddingTop: 64,
    marginBottom: 64,
    "& .exitBtn": {
      width: "90%",
      maxWidth: 800,
      display: "flex",
      justifyContent: "flex-end",
      margin: "8px auto",
      "& button": {
        background: "none",
        color: theme.colors.buttons.sell,
        cursor: "pointer",
        transition: ".3s",
        fontSize: 18,
        border: "none",
        "&:hover": {
          opacity: 0.3,
        },
      },
    },
    "& .links": {
      width: "90%",
      maxWidth: 880,
      margin: "8px auto 0",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      color: theme.colors.text.secondary,

      "& .selectLang": {
        display: "flex",
        alignItems: "center",
        "& label": {
          marginRight: 8,
        },

        "& .languageSelector": {
          position: "relative",
          "& .languagesContainer": {
            position: "absolute",
            top: 0,
            transform: "translate(-70%, -125%)",
            background: theme.colors.background.fore,
            borderRadius: 4,
            boxShadow: theme.defaults.shadow,
            width: 100,
            "& p": {
              height: 20,
              fontSize: 12,
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              transition: ".3s",
              padding: "0 4px",
              "&:hover": {
                background: theme.colors.background.main,
              },
              "&.activeLang": {
                background: theme.colors.background.active,
                cursor: "default",
                "&:hover": {
                  background: theme.colors.background.active,
                },
              },
            },
          },
          "& .selectorBtn": {
            display: "flex",
            alignItems: "center",
            "& svg": {
              fontSize: 16,
            },
          },
        },
      },

      "& .legalLinks": {
        "& a": {
          fontSize: 12,
          color: theme.colors.text.primary,
          marginLeft: 40,
          textDecoration: "none",
        },
      },
    },
  },
  cancelConfirmation: {
    width: "95%",
    maxWidth: 720,
    background: theme.colors.background.lightFore,
    borderRadius: 2,
    margin: 0,
    "& .cancelHeader": {
      background: theme.colors.background.fore,
      height: 75,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& p": {
        color: theme.colors.text.primary,
        fontSize: 18,
      },
      "&.profileHeader": {
        fontWeight: 600,
      },
    },
    "& .cancelBody": {
      padding: "40px 24px 40px 60px",
      [theme.breakpoints.down(400)]: {
        padding: 24,
      },
      "& p": {
        color: theme.colors.text.primary,
        fontSize: 14,
        // fontFamily: "Titillium Web",
        lineHeight: "21px",
      },
      "& .btns": {
        display: "flex",
        justifyContent: "flex-end",
        widht: "100%",
        marginTop: 32,
        "& button": theme.defaults.roundBtn,
        "& .cancelBtn": {
          background: "none",
          border: `1px solid ${theme.colors.text.primary}`,
          marginRight: 16,
          color: theme.colors.text.primary,
        },
        "& .confirmBtn": {
          background: theme.colors.buttons.green,
          border: "1px solid transparent",
        },
        "&.profileBtns": {
          "& button": {
            padding: "0 12px",
          },
        },
        [theme.breakpoints.down(400)]: {
          justifyContent: "center",
        },
      },
    },
  },
  cancelConfirmationM: {
    width: "95%",
    maxWidth: 300,
    background: theme.colors.background.lightFore,
    borderRadius: 2,
    margin: 0,
    "& .cancelHeader": {
      background: theme.colors.background.fore,
      height: 75,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& p": {
        color: theme.colors.text.primary,
        fontSize: 18,
      },
      "&.profileHeader": {
        fontWeight: 600,
      },
    },
    "& .cancelBody": {
      padding: 40,
      [theme.breakpoints.down(400)]: {
        padding: 24,
      },
      "& p": {
        color: theme.colors.text.primary,
        fontSize: 14,
        // fontFamily: "Titillium Web",
        lineHeight: "21px",
      },
    },
  },
});

export default withStyles(styles)(
  withTranslation()(
    connect(mapStateToProps, mapDispatchToProps)(UpgradeContainer)
  )
);
